// Customer portal entry point
import { carriesPortalCredential, logger } from '@forest/logger';
import { GlobalErrorBoundary } from '@ophelos/ui/components/global-error-boundary';
import { hasAnalyticsConsent } from '@ophelos/shared/hooks/use-cookie-consent';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { AuthProvider } from '@/contexts/customer-auth-context';
import { assertSurfaceModeConfig } from '@/lib/surface-mode';
import './index.css';
import App from './App.tsx';

assertSurfaceModeConfig();

// Initialize Datadog RUM with no consent by default: nothing is collected and
// no cookie is set until the visitor grants analytics via the banner, at which
// point the consent provider calls logger.setTrackingConsent(true).
if (import.meta.env.VITE_DATADOG_CLIENT_TOKEN) {
  const apiOrigin = import.meta.env.VITE_API_ORIGIN;
  const applicationId = import.meta.env.VITE_DATADOG_APPLICATION_ID;
  const clientToken = import.meta.env.VITE_DATADOG_CLIENT_TOKEN;

  if (apiOrigin && applicationId && clientToken) {
    logger.init({
      applicationId,
      clientToken,
      service: 'customer-portal',
      env: import.meta.env.MODE,
      version: import.meta.env.VITE_APP_VERSION,
      allowedTracingUrls: [apiOrigin],
      trackingConsent: hasAnalyticsConsent(),
      sessionReplay: !carriesPortalCredential(window.location.pathname),
    });
  }
}

const clientId = import.meta.env.VITE_WORKOS_CLIENT_ID || '';
const redirectUri = import.meta.env.VITE_WORKOS_REDIRECT_URI || 'https://connect.localhost/auth/callback';

// biome-ignore lint/style/noNonNullAssertion: root element guaranteed to exist in index.html
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <GlobalErrorBoundary>
      <AuthProvider
        clientId={clientId}
        redirectUri={redirectUri}
        devMode={import.meta.env.VITE_WORKOS_DEV_MODE === 'true' || import.meta.env.DEV}
        apiHostname={import.meta.env.VITE_WORKOS_API_HOSTNAME}
      >
        <App />
      </AuthProvider>
    </GlobalErrorBoundary>
  </StrictMode>
);

